import { Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import _ from 'lodash';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ELEMENT_HEIGHT_DEFAULT, MAX_ITEMS_ON_DISPLAY } from 'shared/constants/dropdown';
import { KeyCode } from 'shared/enums';
import { DropdownOption } from 'shared/models';
import Label from '../Label';
import styles from '../MultiselectSearchableDropdown/MultiselectSearchableDropdown.module.css';
import RequiredMessage from '../RequiredMessage/RequiredMessage';
import SearchableSuggestionList from '../SearchableSuggestionList';
import { SkeletonBorder } from '../Skeleton';
import TextInput from '../TextInput/TextInput';

interface MultiSelectSearchableDropdownProps {
  dataQa: string;
  dropdownOptions: DropdownOption[];
  onSelectedValueChange: (val: DropdownOption[]) => void;
  values: DropdownOption[];
  className?: string;
  displayOptionsOnTop?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  onSubmitAlert?: boolean;
  placeholder?: string;
  requiredMessageTitle?: string;
  skeletonLoader?: boolean;
}

const MultiSelectSearchableDropdown: React.FC<MultiSelectSearchableDropdownProps> = ({
  dataQa,
  dropdownOptions,
  onSelectedValueChange,
  values,
  className,
  displayOptionsOnTop,
  errorMessage,
  isDisabled,
  isRequired,
  label,
  onSubmitAlert,
  placeholder,
  requiredMessageTitle,
  skeletonLoader,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [selected, setSelected] = useState<DropdownOption[]>(values);
  const [onHover, setOnHover] = useState<number>(0);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const [options, setOptions] = useState<DropdownOption[]>(dropdownOptions);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionListContainerRef = useRef<HTMLDivElement>(null);
  const suggestionItemRef = useRef<HTMLDivElement>(null);
  const elementHeight =
    suggestionItemRef && suggestionItemRef.current ? suggestionItemRef.current.offsetHeight : ELEMENT_HEIGHT_DEFAULT;

  /**
   * Handle selected values change from parent.
   */
  useEffect(() => {
    if (values) {
      setSelected(values);
    }
  }, [values]);

  /**
   * Set options when they change.
   */
  useEffect(() => {
    setOptions(dropdownOptions);
  }, [dropdownOptions]);

  /**
   * Clear values if input becomes disabled.
   */
  useEffect(() => {
    if (isDisabled) {
      onSelectedValueChange([]);
    }
  }, [isDisabled, onSelectedValueChange]);

  /**
   * Handle event when user clicks on component to show drop down options.
   */
  const dropdownHandler = () => {
    if (!isDisabled) {
      setIsMenuOpen(previousValue => !previousValue);
    }
  };

  /**
   * Handle event when user clicks on X icon on badge to remove selection.
   * @param e - click event.
   */
  const selectorValueRemoval = (e: React.MouseEvent) => {
    if (!isDisabled) {
      e.preventDefault(); // Prevent toggling of menu, just clear selection
      onSelectedValueChange([]);
      setSelected([]);
    }
  };

  /**
   * Handle menu blur event.
   * @param e - event.
   */
  const menuBlurHandler = (e: React.FocusEvent<HTMLDivElement>) => {
    if (suggestionListContainerRef.current && !suggestionListContainerRef.current.contains(e.relatedTarget as Node)) {
      setIsInputFocused(false);
      if (!isDisabled) {
        setInputValue('');
        onSelectedValueChange(selected);
        setIsTouched(selected.length === 0);
        setOnHover(0);
        setIsMenuOpen(false);
      }
    }
  };

  /**
   * Handle input change when user is searching for options.
   * @param e - input value.
   */
  const inputChangeHandler = (e: { target: { value: string } }) => {
    const userInput = e.target.value;
    setIsMenuOpen(true);
    setInputValue(userInput);
    const filteredOptions = dropdownOptions.filter(item => item.value.toLowerCase().includes(userInput.toLowerCase()));
    setOptions(filteredOptions);
    // Ensures the currently active index is always the first option, whenever typing.
    setOnHover(0);
  };

  /**
   * Handle input key strokes when user is searching for options.
   * @param e - key stroke value.
   */
  const inputKeyHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyCode.Down) {
      if (options.length - 1 !== onHover) {
        setOnHover(onHover + 1);
      } else {
        setOnHover(0);
      }
      setIsMenuOpen(true);
    }
    if (e.key === KeyCode.Up) {
      if (onHover !== 0) {
        setOnHover(onHover - 1);
      } else {
        setOnHover(options.length - 1);
      }
      setIsMenuOpen(true);
    }
    if (inputValue === '' && e.key === KeyCode.Backspace) {
      onSelectedValueChange(selected.filter((_item, idx) => idx !== selected.length - 1));
      setIsMenuOpen(false);
    }
    if (e.key === KeyCode.Enter) {
      if (inputValue === '' && !isMenuOpen) {
        setIsMenuOpen(true);
      } else {
        // disallow undefined / null values,
        // as it might crash app if there are no matching search results
        const option = options[onHover];
        if (option) optionHandler(option);
      }
    }
    if (e.key === KeyCode.Tab) {
      setIsMenuOpen(false);
      setInputValue('');
    }

    if (e.key === KeyCode.Escape) {
      setIsMenuOpen(false);
    }
    e.preventDefault();
  };

  // in reference with https://glidewell.atlassian.net/browse/LMS1-8786,
  // resetting the search state to defaults, to avoid unexpected behavior
  const resetDropdownToDefaults = useCallback(() => {
    // TODO add any additional resets here
    setOptions(dropdownOptions);
    setInputValue('');
  }, [dropdownOptions]);

  /**
   * Handle selecting and deselecting items.
   * @param item - item selected.
   */
  const optionHandler = (item: DropdownOption) => {
    const valueIndex = selected.findIndex(ele => ele.value === item.value);
    const newValues = _.cloneDeep(selected);
    if (valueIndex > -1) {
      newValues.splice(valueIndex, 1);
    } else {
      newValues.push(item);
    }
    setSelected(newValues.filter(Boolean));
    setIsInputFocused(true);
    // keeps ability to navigate list with arrows after mouse selection
    inputRef?.current?.focus();
  };

  const isSelected = useCallback(
    (item: DropdownOption): boolean => {
      return selected.some(ele => ele.value === item.value);
    },
    [selected]
  );

  const hasError = errorMessage || (isRequired && !isDisabled && !selected.length && (isTouched || onSubmitAlert));

  const hasOptions = options.length > 0;
  const optionsNodes = hasOptions
    ? options.map((item, index) => {
        const isValueSelected = isSelected(item);
        return (
          <div
            key={index}
            ref={onHover === index ? suggestionItemRef : null}
            className={classNames('cursor-pointer select-none relative py-2 text-gray-900 pl-3', {
              'bg-indigo-600': onHover === index,
              'bg-white': onHover !== index,
            })}
            onMouseOver={() => {
              !isDisabled && setOnHover(index);
            }}
            onMouseMove={() => setOnHover(index)}
            onClick={() => optionHandler(item)}
            data-qa={`${dataQa}Option`}
            data-testid={`${dataQa}Option`}
          >
            <div
              className={classNames('flex flex-1 text-sm', {
                'text-white': onHover === index,
                'text-black': onHover !== index,
                'font-semibold': isValueSelected,
                'font-normal': !isValueSelected,
              })}
            >
              <CheckIcon
                className={classNames('justify-center h-100 mr-2 flex-shrink-0', {
                  'text-white': onHover === index && isValueSelected,
                  'text-indigo-600': onHover !== index && isValueSelected,
                  'text-transparent': !isValueSelected,
                })}
                aria-hidden="true"
                style={{ width: '20px' }}
              />
              <div className="justify-center h-100 whitespace-nowrap overflow-hidden">
                <div className="truncate text-ellipsis">{item.label}</div>
              </div>
            </div>
          </div>
        );
      })
    : [
        <div key="NoOptionsItem" className="bg-white text-gray-400 text-center p-1">
          No options
        </div>,
      ];

  return (
    <div className="relative">
      {label && (
        <Label data-testid="label" required={isRequired}>
          {label}
        </Label>
      )}
      {skeletonLoader ? (
        <SkeletonBorder className="h-8 pr-10" />
      ) : (
        <button
          className={classNames(
            className,
            'flex-1 rounded-md shadow-sm focus:outline-none border w-full relative px-2 py-1',
            {
              'bg-gray-50 border-gray-300 text-gray-400': isDisabled,
              'bg-white border-gray-300': !isDisabled,
              'focus:ring-red-500 focus:border-red-500 border-red-500': hasError,
            }
          )}
          onClick={dropdownHandler}
          data-qa={`${dataQa}Button`}
          data-testid={`${dataQa}Button`}
          type="button"
        >
          {selected.length === 0 && (
            <span className={classNames('float-left inline-flex truncate lg:w-3/4')}>
              <span className="w-full text-left text-gray-500 text-sm p-1">{placeholder}</span>
            </span>
          )}
          <div className="flex flex-1 flex-wrap w-9/12">
            {selected.length > 0 && (
              <div className="flex items-center text-blue-800 bg-blue-100 rounded-xl text-xs p-1 pl-2 pr-1 my-0.5 mr-0.5">
                <span className="justify-center h-100 whitespace-nowrap overflow-hidden">
                  {selected.length > 0 && `${selected.length} Selected`}
                </span>
                <XMarkIcon className="h-3 w-3 ml-1 text-blue-600" onClick={selectorValueRemoval} />
              </div>
            )}
          </div>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          {hasError && (
            <div className="absolute inset-y-0 right-0 pr-9 flex items-center pointer-events-none">
              {<ExclamationCircleIcon className="h-5 text-red-500" />}
            </div>
          )}
        </button>
      )}

      <Transition
        show={isDisabled ? false : isMenuOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        afterLeave={resetDropdownToDefaults}
        afterEnter={() => {
          setIsInputFocused(true);
          inputRef.current?.focus();
        }}
      >
        <div
          className={classNames(
            { 'bottom-full mb-1': displayOptionsOnTop },
            'absolute z-40 w-full mt-1 bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none'
          )}
          ref={suggestionListContainerRef}
          data-qa={`${dataQa}Options`}
          data-testid={`${dataQa}Options`}
          tabIndex={0}
          onBlur={menuBlurHandler}
        >
          <TextInput
            inputClassNames={classNames(
              'sticky relative p-3 mx-2 mb-2 mt-1 rounded-md shadow-sm focus:outline-none focus:ring-2 border flex flex-1 text-sm border-none shadow-none outline-none placeholder:text-gray-400:placeholder:text-black',
              {
                'bg-gray-50 border-gray-300 text-gray-400': isDisabled,
                'bg-white border-gray-300': !isDisabled,
                'focus:ring-red-500 focus:border-red-500 border-red-500': hasError,
                'focus:ring-indigo-500 focus:border-indigo-500 border-gray-300':
                  !isRequired || !onSubmitAlert || !!selected.length,
                'ring-indigo-500 border-indigo-500 outline-none ring-1': isInputFocused,
              },
              styles['input-wrapper']
            )}
            placeholder={'Search'}
            id={'multiSelectSearchableInput'}
            value={inputValue}
            inputRef={inputRef}
            disabled={isDisabled}
            handleInputChange={inputChangeHandler}
            handleInputKeyDown={inputKeyHandler}
            data-qa={dataQa}
            data-testid={dataQa}
            type="text"
          />
          <div
            className="overflow-y-auto overscroll-auto overflow-auto"
            style={{ maxHeight: MAX_ITEMS_ON_DISPLAY * elementHeight + 'px' }}
          >
            <SearchableSuggestionList
              filteredSuggestionNodes={optionsNodes}
              activeSuggestionIndex={onHover}
              dataQa={dataQa}
              suggestionItemRef={suggestionItemRef}
            />
          </div>
        </div>
      </Transition>
      {hasError && <RequiredMessage fieldName={label || requiredMessageTitle} errorMessage={errorMessage} />}
    </div>
  );
};

export default MultiSelectSearchableDropdown;
